import React, { createContext, useContext, useState, useEffect } from 'react';
import { ROLES } from './permissions';

/**
 * Contexto para manejar el usuario actual y su rol en el sistema
 */
const UserContext = createContext();

/**
 * Hook personalizado para acceder al contexto de usuario
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser debe ser usado dentro de un UserProvider');
  }
  return context;
};

/**
 * Provider del contexto de usuario
 */
export const UserProvider = ({ children }) => {
  // Estado del usuario actual
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar usuario desde localStorage al iniciar
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('currentUser');
        console.log('=== DEBUG USER CONTEXT - CARGAR USUARIO ===');
        console.log('storedUser (raw):', storedUser);
        
        if (storedUser) {
          const user = JSON.parse(storedUser);
          console.log('Usuario parseado:', user);
          console.log('  - ID:', user.id);
          console.log('  - Nombres:', user.nombres);
          console.log('  - Apellidos:', user.apellidos);
          console.log('  - roleId:', user.roleId);
          console.log('  - rol:', user.rol);
          setCurrentUser(user);
        } else {
          console.log('No hay usuario en localStorage');
        }
        console.log('==========================================');
      } catch (error) {
        console.error('Error al cargar usuario:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  /**
   * Establece el usuario actual y lo guarda en localStorage
   * @param {Object} user - Objeto de usuario con al menos { id, nombres, apellidos, id_Rol }
   */
  const login = (user) => {
    console.log('=== DEBUG USER CONTEXT - LOGIN ===');
    console.log('Usuario recibido en login:', user);
    console.log('Inspeccionando todas las propiedades:');
    console.log('  - user.id:', user.id);
    console.log('  - user.Id:', user.Id);
    console.log('  - user.identificacion:', user.identificacion);
    console.log('  - user.Identificacion:', user.Identificacion);
    console.log('  - user.id_Rol (camelCase del backend):', user.id_Rol);
    console.log('  - user.Id_Rol (PascalCase):', user.Id_Rol);
    console.log('  - user.roleId:', user.roleId);
    console.log('  - user.rol:', user.rol);
    console.log('  - user.Rol:', user.Rol);
    
    // Manejar tanto PascalCase (backend C# antiguo) como camelCase (backend configurado)
    const userId = user.id || user.Id;
    const userIdRol = user.id_Rol || user.Id_Rol || user.roleId;
    
    if (!user || !userId || !userIdRol) {
      console.error('Usuario inválido - faltan datos requeridos:');
      console.log('  - tiene user?:', !!user);
      console.log('  - userId extraído:', userId);
      console.log('  - userIdRol extraído:', userIdRol);
      console.log('==================================');
      return;
    }

    const userData = {
      id: userId,
      identificacion: user.identificacion || user.Identificacion,
      nombres: user.nombres || user.Nombres,
      apellidos: user.apellidos || user.Apellidos,
      roleId: userIdRol,
      rol: user.rol?.rol || user.Rol?.rol || user.roleName || 'Usuario'
    };

    console.log('userData creado:', userData);
    console.log('  - id:', userData.id);
    console.log('  - roleId asignado:', userData.roleId);
    console.log('  - rol:', userData.rol);
    
    setCurrentUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
    console.log('Usuario guardado en localStorage');
    console.log('==================================');
  };

  /**
   * Cierra la sesión del usuario actual
   */
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    // Redirigir al login se maneja en el componente que llama a logout
  };

  /**
   * Obtiene el ID del rol del usuario actual
   * @returns {number|null}
   */
  const getUserRoleId = () => {
    // Primero intenta desde el estado
    let roleId = currentUser?.roleId || null;
    
    // Si no está en el estado, intenta desde localStorage
    if (roleId === null) {
      try {
        const storedUser = localStorage.getItem('currentUser');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          roleId = user.roleId || null;
        }
      } catch (error) {
        console.error('Error al leer roleId desde localStorage:', error);
      }
    }
    
    console.log('getUserRoleId() llamado:', {
      currentUser,
      roleId,
      hasCurrentUser: !!currentUser,
      fromLocalStorage: currentUser === null && roleId !== null
    });
    return roleId;
  };

  /**
   * Verifica si el usuario actual es administrador
   * @returns {boolean}
   */
  const isAdmin = () => {
    const roleId = getUserRoleId();
    return roleId === ROLES.ADMINISTRADOR;
  };

  /**
   * Verifica si el usuario actual es tesorero
   * @returns {boolean}
   */
  const isTesorero = () => {
    const roleId = getUserRoleId();
    return roleId === ROLES.TESORERO;
  };

  /**
   * Verifica si el usuario actual es donante
   * @returns {boolean}
   */
  const isDonante = () => {
    const roleId = getUserRoleId();
    return roleId === ROLES.DONANTE;
  };

  /**
   * Verifica si hay un usuario autenticado
   * @returns {boolean}
   */
  const isAuthenticated = () => {
    if (currentUser !== null) return true;
    
    // Verificar también en localStorage
    try {
      const storedUser = localStorage.getItem('currentUser');
      return storedUser !== null;
    } catch {
      return false;
    }
  };

  const value = {
    currentUser,
    isLoading,
    login,
    logout,
    getUserRoleId,
    isAdmin,
    isTesorero,
    isDonante,
    isAuthenticated
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
